import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	activateProSubscriptionDirectUsersMeProSubscriptionActivatePostMutation,
	createProCheckoutUsersMeProSubscriptionCheckoutPostMutation,
	getSavedPaymentMethodMePaymentMethodGetOptions,
	getBundlePropertyBundlesBundleIdGetOptions,
	readUsersMeUsersMeGetQueryKey,
} from '@/client/@tanstack/react-query.gen';
import { SavedPaymentMethodResponse, User } from '@/client';
import { useAuth } from '@/context/AuthContext';
import { hasProActiveSubscription, SubscriptionStatuses, SubscriptionTypes } from '@/utils/userUtils';
import { useRedirectPending } from '@/hooks/useRedirectPending';
import { toaster } from '@/components/ui/toaster';

const PRO_BUNDLE_ID = 'bundle-professional';

/**
 * Manages Pro subscription checkout: shows a saved-card confirmation modal when the
 * user already has a payment method on file, and falls back to Stripe hosted checkout otherwise.
 */
export const useProSubscription = () => {
	const { user, isTermsCompliant } = useAuth();
	const queryClient = useQueryClient();
	const isSubscribed = hasProActiveSubscription(user);
	const canCheckout = isTermsCompliant && !!user?.first_name;

	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
	const [savedCard, setSavedCard] = useState<SavedPaymentMethodResponse | null>(null);
	const [pendingAmountCents, setPendingAmountCents] = useState(0);
	const [isPreparingCheckout, setIsPreparingCheckout] = useState(false);

	const { isRedirecting, setIsRedirecting } = useRedirectPending();

	const { mutateAsync: createCheckout, isPending: isCreatingCheckout } = useMutation(
		createProCheckoutUsersMeProSubscriptionCheckoutPostMutation(),
	);

	const { mutateAsync: activateSubscription, isPending: isActivating } = useMutation(
		activateProSubscriptionDirectUsersMeProSubscriptionActivatePostMutation(),
	);

	const runHostedCheckout = useCallback(async () => {
		try {
			const session = await createCheckout({});
			if (session?.url) {
				setIsRedirecting(true);
				window.location.href = session.url;
			}
		} catch (error) {
			console.error('Pro checkout failed:', error);
		}
	}, [createCheckout, setIsRedirecting]);

	const initiateCheckout = useCallback(async () => {
		if (!canCheckout) return;
		setIsPreparingCheckout(true);
		try {
			const [paymentMethod, proBundle] = await Promise.all([
				queryClient.fetchQuery(getSavedPaymentMethodMePaymentMethodGetOptions()),
				queryClient.fetchQuery(
					getBundlePropertyBundlesBundleIdGetOptions({ path: { bundle_id: PRO_BUNDLE_ID } }),
				),
			]);
			if (!paymentMethod.has_saved_card || proBundle.price_amount_cents == null) {
				await runHostedCheckout();
				return;
			}
			setSavedCard(paymentMethod);
			setPendingAmountCents(proBundle.price_amount_cents);
			setIsConfirmationOpen(true);
		} catch (error) {
			console.error('Failed to prepare Pro checkout:', error);
			await runHostedCheckout();
		} finally {
			setIsPreparingCheckout(false);
		}
	}, [canCheckout, queryClient, runHostedCheckout]);

	const confirmCheckout = useCallback(async () => {
		try {
			const result = await activateSubscription({});
			if (result.status === 'requires_action' && result.redirect_url) {
				setIsConfirmationOpen(false);
				setIsRedirecting(true);
				window.location.href = result.redirect_url;
				return;
			}
			if (result.status === 'active') {
				queryClient.setQueryData<User | undefined>(readUsersMeUsersMeGetQueryKey(), (currentUser) =>
					currentUser
						? {
								...currentUser,
								user_subscription: {
									...(currentUser.user_subscription ?? {}),
									status: SubscriptionStatuses.Active,
									type: SubscriptionTypes.Pro,
								},
							}
						: currentUser,
				);
				setIsConfirmationOpen(false);
				setSavedCard(null);
				await queryClient.invalidateQueries({ queryKey: readUsersMeUsersMeGetQueryKey() });
				return;
			}
			toaster.create({ title: 'Subscription activation failed. Please try again.', type: 'error' });
		} catch (error) {
			console.error('Pro subscription activation failed:', error);
			toaster.create({ title: 'Failed to activate subscription. Please try again.', type: 'error' });
		}
	}, [activateSubscription, queryClient, setIsRedirecting]);

	const cancelCheckout = useCallback(() => {
		setIsConfirmationOpen(false);
		setSavedCard(null);
	}, []);

	return {
		isSubscribed,
		canCheckout,
		isConfirmationOpen,
		savedCard,
		pendingAmountCents,
		initiateCheckout,
		confirmCheckout,
		cancelCheckout,
		isCheckingOut: isPreparingCheckout || isCreatingCheckout || isActivating || isRedirecting,
	};
};
